import { Sparkles, X, MapPin, Pencil, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Compact result list for the AI natural-language search. Every "why matched"
 * bullet must be grounded in real contact data — the backend already enforces
 * this. This component just renders what the server returned.
 */
export default function AiSearchResults({
  results,
  contactsById,
  appliedFilters,
  degraded,
  onOpen,
  onEdit,
  onShowOnMap,
  onShowAllOnMap,
  onClose,
}) {
  if (!results || results.length === 0) {
    return (
      <div className="mx-5 mt-3 mb-2 p-4 rounded-md border border-white/10 bg-black/40 text-white/70 text-sm">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-[#00e5ff]" />
          <span className="font-mono uppercase tracking-wider text-xs text-[#00e5ff]">
            AI Search
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-white/50 hover:text-white"
            aria-label="Close AI results"
            data-testid="ai-search-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {degraded ? (
          <div className="text-amber-300/90">
            AI search is temporarily unavailable. Try a keyword search — normal
            filters still work.
          </div>
        ) : (
          <div>No contacts match this query.</div>
        )}
        {appliedFilters && appliedFilters.length > 0 && (
          <div className="mt-2 text-xs text-white/50">
            Interpreted as: {appliedFilters.join(" · ")}
          </div>
        )}
      </div>
    );
  }

  const withLocation = results.filter((r) => {
    const c = contactsById.get(r.contact_id);
    return c && Number.isFinite(c.lat) && Number.isFinite(c.lng);
  });

  return (
    <div
      className="mx-5 mt-3 mb-2 rounded-md border border-white/10 bg-black/40"
      data-testid="ai-search-results"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <Sparkles className="h-4 w-4 text-[#00e5ff]" />
        <span className="font-mono uppercase tracking-wider text-xs text-[#00e5ff]">
          AI Search · {results.length} result{results.length === 1 ? "" : "s"}
        </span>
        {withLocation.length > 0 && (
          <button
            type="button"
            onClick={() => onShowAllOnMap?.(withLocation.map((r) => r.contact_id))}
            className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#00e5ff] hover:text-white px-2 py-1 border border-[#00e5ff]/40 rounded-sm"
            data-testid="ai-search-show-on-map"
            title="Show only these results on the map"
          >
            Show on map
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className={`text-white/50 hover:text-white ${withLocation.length > 0 ? "" : "ml-auto"}`}
          aria-label="Close AI results"
          data-testid="ai-search-close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {appliedFilters && appliedFilters.length > 0 && (
        <div className="px-3 py-2 border-b border-white/10 text-[11px] font-mono text-white/60">
          Interpreted as: {appliedFilters.join(" · ")}
        </div>
      )}

      <div className="max-h-[46vh] overflow-y-auto divide-y divide-white/5">
        {results.map((r) => {
          const c = contactsById.get(r.contact_id);
          if (!c) return null;
          const locBits = [c.town, c.region, c.country].filter(Boolean).join(" · ");
          const hasLoc = Number.isFinite(c.lat) && Number.isFinite(c.lng);
          return (
            <div
              key={r.contact_id}
              className="px-3 py-3 flex gap-3"
              data-testid={`ai-result-${r.contact_id}`}
            >
              <div className="shrink-0">
                {c.avatar ? (
                  <img
                    src={c.avatar}
                    alt=""
                    className="w-11 h-11 rounded-full object-cover border border-white/20"
                    draggable={false}
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-white/5 border border-white/20 flex items-center justify-center text-[#f5d300] text-sm font-semibold">
                    {(c.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-white truncate text-sm">
                    {c.name}
                  </div>
                  {r.score != null && (
                    <span
                      className="text-[9px] font-mono uppercase text-white/40"
                      title="Match score"
                    >
                      {Math.round(r.score * 100)}%
                    </span>
                  )}
                </div>
                {locBits && (
                  <div className="text-[11px] font-mono text-white/50 truncate">
                    {locBits}
                  </div>
                )}
                {r.reasons && r.reasons.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 text-[11px] text-white/70">
                    {r.reasons.slice(0, 4).map((rr, i) => (
                      <li
                        key={i}
                        className="leading-snug"
                        data-testid={`ai-result-reason-${r.contact_id}-${i}`}
                      >
                        <span className="text-[#00e5ff] mr-1">•</span>
                        {rr}
                      </li>
                    ))}
                  </ul>
                )}
                {r.snippet && (
                  <div
                    className="mt-1.5 text-[11px] italic text-white/50 border-l border-white/20 pl-2"
                    data-testid={`ai-result-snippet-${r.contact_id}`}
                  >
                    “{r.snippet}”
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider bg-transparent border-white/15 text-white/80 hover:text-white"
                    onClick={() => onOpen?.(c)}
                    data-testid={`ai-result-open-${r.contact_id}`}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" /> Open
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider bg-transparent border-white/15 text-white/80 hover:text-white"
                    onClick={() => onEdit?.(c)}
                    data-testid={`ai-result-edit-${r.contact_id}`}
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  {hasLoc && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider bg-transparent border-[#00e5ff]/40 text-[#00e5ff] hover:text-white hover:border-[#00e5ff]"
                      onClick={() => onShowOnMap?.(c)}
                      data-testid={`ai-result-map-${r.contact_id}`}
                    >
                      <MapPin className="h-3 w-3 mr-1" /> Show on map
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
